import Phaser from 'phaser'
import { GAME_W, GAME_H, TILE } from '../config'
import DialogUI from '../systems/Dialog'
import {
  updateEnemyAI,
  EnemyWithAI,
  Archer,
  Mage,
  Brute,
  makeArcher,
  makeMage,
  makeBrute,
  makeEnemy,
  updateArcherAI,
  updateMageAI,
  updateBruteAI,
  EnemyDialogs,
  EnemyOverrides,
} from '../systems/EnemyAI'
import { EnemySpeech } from '../systems/EnemySpeech'
import { buildMapFromJSON } from '../systems/Tilemap'
import type { TileDef, MapData } from '../types/tileset'
import {
  createPlayerAnimations,
  createEnemyAnimations,
  getDirectionFromVelocity
} from '../systems/AnimationManager'
import { createNPCManager, NPCManagerHandle } from '../systems/NPCManager'
import type { NPCDef, NPCSpawn } from '../types/NPCTypes'
import { updateHomingOrbs, updateRotatingProjectiles, FireBall, type Projectile } from '../systems/Projectile'
import { events } from '../systems/Events'
import { EventTriggerManager } from '../systems/EventTriggerManager'
import { Boss } from '../types/BossTypes'
import { makeBoss, updateBossAI } from '../systems/BossAI'
import { AudioBus } from '../systems/AudioBus'
import { BossHpUI } from '../systems/BossHpUI'
import { BossSpeechBubble } from '../systems/BossSpeechBubble'
import { CutinSystem } from '../systems/CutinSystem'
import { logger } from '../utils/Logger'
import { VirtualJoystick } from '../ui/VirtualJoystick'
import { BGMManager } from '../systems/BGMManager'
import { PauseMenu } from '../ui/PauseMenu'
import { GameStateManager } from '../systems/GameStateManager'
import { GameFlowManager } from '../systems/GameFlowManager'
import { PortalManager } from '../systems/PortalManager'
import type { FullPortalData } from '../systems/PortalManager'
import type { ThenAction } from '../types/GameFlowTypes'
import { advanceClock, setClockSpeed } from '../systems/GameClock'
import { setDangerLevel, resetWorldState } from '../systems/WorldState'
import { resolveText } from '../utils/LocaleManager'

export default class MainScene extends Phaser.Scene {
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys
  private wasd!: { up: Phaser.Input.Keyboard.Key; down: Phaser.Input.Keyboard.Key; left: Phaser.Input.Keyboard.Key; right: Phaser.Input.Keyboard.Key }
  private spaceKey!: Phaser.Input.Keyboard.Key
  private player!: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody
  private npcManager!: NPCManagerHandle
  private enemies: EnemyWithAI[] = []
  private archers: Archer[] = []
  private mages: Mage[] = []
  private brutes: Brute[] = []
  private walls?: Phaser.Physics.Arcade.StaticGroup
  private mapAnimSprites: Phaser.GameObjects.Sprite[] = []
  private mapLayerSprites: Phaser.GameObjects.Sprite[] = []
  private hitbox!: Phaser.GameObjects.Rectangle & { body: Phaser.Physics.Arcade.Body }
  private ui!: DialogUI
  private playerDirection: string = 'down'
  private isAttacking: boolean = false
  private isSpecialAttacking: boolean = false
  private isRightMouseHeld: boolean = false
  private projectiles!: Phaser.Physics.Arcade.Group
  private hpText!: Phaser.GameObjects.Text
  private isGameOver: boolean = false
  private gameOverText?: Phaser.GameObjects.Text
  private isGameCleared: boolean = false
  private eventTriggerManager?: EventTriggerManager
  private portalManager?: PortalManager
  private currentMapId: string = ''
  private currentMapData: Record<string, unknown> | null = null
  private colliders: Phaser.Physics.Arcade.Collider[] = []

  // ボス関連
  private boss: Boss | null = null
  private audioBus!: AudioBus
  private bossHpUI: BossHpUI | null = null
  private bossSpeechBubble!: BossSpeechBubble
  private cutinSystem!: CutinSystem

  // マウス/タッチ操作UI
  private virtualJoystick: VirtualJoystick | null = null
  private mobileAimVector: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, 0)
  private isMouseMoving: boolean = false
  private mouseWorldX: number = 0
  private mouseWorldY: number = 0
  private isMobileSpecialHeld: boolean = false
  private readonly isTouchDevice: boolean = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // 火炎放射
  private firePool!: Phaser.Physics.Arcade.Group
  private lastFireTime: number = 0
  private flameIsPlaying: boolean = false
  private readonly FIRE_INTERVAL = 50
  private readonly FIRE_SPEED    = 600
  private readonly FIRE_RANGE    = 333

  // タイトル画面・ポーズメニュー関連
  private bgmManager?: BGMManager
  private pauseMenu?: PauseMenu
  private escKey?: Phaser.Input.Keyboard.Key

  // ゲームフロー管理
  private gameFlowManager!: GameFlowManager
  private introLaunched: boolean = false

  // UIカメラ（ゲームカメラとは別にUI要素をzoom=1で描画）
  private uiCamera!: Phaser.Cameras.Scene2D.Camera
  private logDownloadButton?: Phaser.GameObjects.Text

  constructor() { super('MainScene') }

  create() {
    // シーン再起動時のステイル状態リセット（コンストラクタは再呼び出しされないため必須）
    this.enemies = []
    this.archers = []
    this.mages = []
    this.brutes = []
    this.colliders = []
    this.boss = null
    this.bossHpUI = null
    this.eventTriggerManager = undefined
    this.portalManager = undefined
    this.walls = undefined
    this.isGameOver = false
    this.isGameCleared = false
    this.currentMapId = ''
    this.currentMapData = null
    this.introLaunched = false

    // アニメーション定義
    createPlayerAnimations(this, 'hero')
    createEnemyAnimations(this, 'blob',   'solder')
    createEnemyAnimations(this, 'archer', 'vamp1')
    createEnemyAnimations(this, 'mage',   'succubus')
    createEnemyAnimations(this, 'brute',  'mage')
    // enemy-defsで指定されたspriteKeyごとにアニメーションセットを追加生成
    {
      const defsForAnims = (this.cache.json.get('enemy-defs') as { spriteKey?: string }[]) || []
      for (const d of defsForAnims) {
        if (d.spriteKey && this.textures.exists(d.spriteKey)) {
          createEnemyAnimations(this, d.spriteKey, d.spriteKey)
        }
      }
    }

    // プレイヤー（初期位置はダミー、switchMap後に正式に配置される）
    this.player = this.physics.add.sprite(40 * TILE, 40 * TILE, 'hero')
    ;(this.player as any).speed = 260
    ;(this.player as any).hp = 100
    this.player.setCollideWorldBounds(true)
    this.player.setDepth(10)
    this.player.play('hero-idle-down')

    // 飛び道具グループ初期化
    this.projectiles = this.physics.add.group()
    this.firePool = this.physics.add.group({ maxSize: 20, runChildUpdate: false })

    // 飛び道具 vs プレイヤーの永続オーバーラップ
    // physics.add.overlap(group, sprite, cb) の引数順: cb は (sprite, groupMember)
    this.physics.add.overlap(this.projectiles, this.player, (_playerObj, projObj) => {
      const proj = projObj as Projectile
      if (!proj.active) return
      if (this.player.getData('hitCool') || this.isGameOver) return
      const damage = (proj.damage as number | undefined) ?? 1
      type PlayerWithHp = Phaser.Types.Physics.Arcade.SpriteWithDynamicBody & { hp: number }
      const oldHP = (this.player as PlayerWithHp).hp
      const newHP = Math.max(0, oldHP - damage)
      ;(this.player as PlayerWithHp).hp = newHP
      this.updateHPDisplay()
      this.player.setTint(0xff4444)
      this.audioBus.playSe('se_player_hit', { volume: 0.8 })
      this.player.setData('hitCool', true)
      this.time.delayedCall(100, () => this.player.clearTint())
      this.time.delayedCall(500, () => this.player.setData('hitCool', false))
      proj.destroy()
      if (newHP <= 0) this.triggerGameOver()
    })

    // ボスシステム初期化
    this.audioBus = new AudioBus(this)
    this.bossSpeechBubble = new BossSpeechBubble(this)
    this.cutinSystem = new CutinSystem(this)

    // DialogUI初期化
    this.ui = new DialogUI(this)

    // NPCマネージャー初期化（マップロード時にNPCを読み込む）
    this.npcManager = createNPCManager(this, this.ui)

    // HP表示を先に作成
    this.createHPDisplay()

    // ログダウンロードボタンを作成（開発時のみ）
    if (import.meta.env.DEV) {
      this.createLogDownloadButton()
    }

    // 仮想ジョイスティックと攻撃ボタンを作成
    this.createVirtualControls()

    this.cameras.main.startFollow(this.player, true, 0.12, 0.12)
    this.cameras.main.setZoom(1.5)
    this.cameras.main.setBackgroundColor('#000000')

    this.cursors = this.input.keyboard!.createCursorKeys()
    const kb = this.input.keyboard!
    this.wasd = {
      up:    kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left:  kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    }
    this.spaceKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE)

    // 攻撃ヒットボックス
    this.hitbox = this.add.rectangle(0, 0, 48, 48, 0xffffff, 0) as any
    this.physics.add.existing(this.hitbox)
    ;(this.hitbox.body as Phaser.Physics.Arcade.Body).setEnable(false)

    // アニメ完了時のイベント
    this.player.on(Phaser.Animations.Events.ANIMATION_COMPLETE, (anim: Phaser.Animations.Animation) => {
      if (anim.key.startsWith('hero-atk-')) {
        this.isAttacking = false
        if (this.isRightMouseHeld || this.isMobileSpecialHeld) {
          this.isSpecialAttacking = true
          this.player.play(`hero-special-${this.playerDirection}`, true)
        } else {
          this.player.play(`hero-idle-${this.playerDirection}`)
        }
      }
    })

    // 攻撃判定とアニメの同期
    this.player.on(Phaser.Animations.Events.ANIMATION_UPDATE, (anim: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
      if (anim.key.startsWith('hero-atk-')) {
        const f = frame.index % 4
        const active = (f === 1 || f === 2)
        ;(this.hitbox.body as Phaser.Physics.Arcade.Body).setEnable(active)
      }
    })

    this.spaceKey.on('down', () => {
      if (this.ui.visible) {
        this.ui.next()
        return
      }

      if (this.npcManager.tryInteract(this.player, 80)) {
        return
      }

      this.doAttack()
    })

    // GameFlowManager初期化（BGMManager より先に初期化する）
    this.gameFlowManager = new GameFlowManager(this)
    setClockSpeed(this.gameFlowManager.getClockSpeed())

    // BGMManagerの初期化
    this.bgmManager = new BGMManager(this, this.audioBus, this.gameFlowManager)
    console.log('[MainScene] BGMManager initialized')

    // PauseMenuの初期化
    this.pauseMenu = new PauseMenu(this)
    this.pauseMenu.setBackToTitleCallback(() => {
      GameStateManager.reset(this)
      this.scene.stop()
      this.scene.start('TitleScene')
    })
    console.log('[MainScene] PauseMenu initialized')

    // Escキーの登録
    this.escKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC)
    console.log('[MainScene] ESC key registered')

    // シャットダウン時のクリーンアップ
    this.events.once('shutdown', () => {
      GameStateManager.reset(this)
      if (this.bgmManager) {
        this.bgmManager.destroy()
      }
      if (this.pauseMenu) {
        this.pauseMenu.destroy()
      }
      this.enemies.forEach(en => en.speech?.destroy())
      this.archers.forEach(ar => ar.speech?.destroy())
      this.mages.forEach(mg => mg.speech?.destroy())
      this.brutes.forEach(br => br.speech?.destroy())
      console.log('[MainScene] Cleanup complete')
    })

    // UIカメラのセットアップ（ゲームワールドとUI描画を分離）
    this.setupCameras()
  }

  private setupCameras(): void {
    this.uiCamera = this.cameras.add(0, 0, GAME_W, GAME_H, false, 'ui')
    this.uiCamera.setZoom(1)

    // UI要素のリストを収集（メインカメラから除外する）
    const uiObjects: Phaser.GameObjects.GameObject[] = [
      this.ui.getContainer(),
      this.hpText,
      this.cutinSystem.getContainer(),
      this.pauseMenu!.getContainer(),
    ]
    if (this.virtualJoystick) {
      uiObjects.push(this.virtualJoystick.getContainer())
    }
    if (this.logDownloadButton) {
      uiObjects.push(this.logDownloadButton)
    }

    // ゲームカメラはUI要素を描画しない
    this.cameras.main.ignore(uiObjects)

    // UIカメラはゲームワールドオブジェクトを描画しない（現時点のスナップショット）
    const worldObjects = this.children.list.filter(go => !uiObjects.includes(go))
    this.uiCamera.ignore(worldObjects)
  }

  // ゲームワールドオブジェクトをUIカメラの描画対象から除外する
  addWorldObject(go: Phaser.GameObjects.GameObject): void {
    if (this.uiCamera) this.uiCamera.ignore(go)
  }

  private createHPDisplay() {
    const initialText = 'HP: 100/100'
    this.hpText = this.add.text(20, 20, initialText, {
      fontSize: '32px',
      color: '#ffffff',
      fontFamily: 'Courier New, monospace',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: '#000000',
        blur: 4,
        fill: true
      }
    })

    this.hpText.setScrollFactor(0, 0)
    this.hpText.setOrigin(0, 0)
    this.hpText.setDepth(10000)

    console.log('HP Display created at position:', this.hpText.x, this.hpText.y)

    this.updateHPDisplay()
  }

  private createLogDownloadButton() {
    this.logDownloadButton = this.add.text(GAME_W - 200, 20, '[LOG DL]', {
      fontSize: '24px',
      color: '#00ff00',
      fontFamily: 'Courier New, monospace',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      backgroundColor: '#00000088',
      padding: { x: 12, y: 8 }
    })

    this.logDownloadButton.setScrollFactor(0, 0)
    this.logDownloadButton.setOrigin(0, 0)
    this.logDownloadButton.setDepth(10000)
    this.logDownloadButton.setInteractive({ useHandCursor: true })

    this.logDownloadButton.on('pointerover', () => {
      this.logDownloadButton!.setColor('#ffff00')
    })

    this.logDownloadButton.on('pointerout', () => {
      this.logDownloadButton!.setColor('#00ff00')
    })

    this.logDownloadButton.on('pointerdown', () => {
      console.log('[MainScene] Log download button clicked')
      logger.downloadLogs()
      this.logDownloadButton!.setColor('#ff00ff')
      this.time.delayedCall(200, () => {
        this.logDownloadButton!.setColor('#00ff00')
      })
    })

    console.log('[MainScene] Log download button created')
  }

  private createVirtualControls() {
    if (this.isTouchDevice) {
      // 左半分: フローティングジョイスティック
      this.virtualJoystick = new VirtualJoystick(this)
      // 右半分: 攻撃ゾーン
      this.setupMobileAttackZone()
    } else {
      // PC: マウスクリック移動 + 右クリック特殊攻撃
      this.setupMouseMovement()
    }
    console.log('[MainScene] Virtual controls created, touch:', this.isTouchDevice)
  }

  private setupMobileAttackZone() {
    const LONG_PRESS_MS = 300
    const AIM_DEAD_ZONE = 15 // この距離を超えたら照準更新
    let rightPointerId: number | null = null
    let rightStartX = 0
    let rightStartY = 0
    let longPressTimer: ReturnType<typeof setTimeout> | null = null
    let isLongPressed = false

    const cancelTimer = () => {
      if (longPressTimer !== null) {
        clearTimeout(longPressTimer)
        longPressTimer = null
      }
    }

    const endSpecial = () => {
      isLongPressed = false
      this.isMobileSpecialHeld = false
      this.mobileAimVector.set(0, 0)
      if (this.isSpecialAttacking) {
        this.isSpecialAttacking = false
        this.player.play(`hero-idle-${this.playerDirection}`)
      }
    }

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.x < this.scale.width / 2 || rightPointerId !== null) return

      // ダイアログ表示中はセリフ送り
      if (this.ui.visible) {
        this.ui.next()
        return
      }
      // NPC近くならインタラクト
      if (this.npcManager.tryInteract(this.player, 80)) return

      rightPointerId = pointer.id
      rightStartX = pointer.x
      rightStartY = pointer.y
      isLongPressed = false
      this.mobileAimVector.set(0, 0)

      longPressTimer = setTimeout(() => {
        longPressTimer = null
        isLongPressed = true
        this.isMobileSpecialHeld = true
        this.performAttack()
      }, LONG_PRESS_MS)
    })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.id !== rightPointerId || !isLongPressed) return
      const dx = pointer.x - rightStartX
      const dy = pointer.y - rightStartY
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist > AIM_DEAD_ZONE) {
        this.mobileAimVector.set(dx / dist, dy / dist)
      }
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.id !== rightPointerId) return
      rightPointerId = null

      if (longPressTimer !== null) {
        // 短タップ → 通常攻撃
        cancelTimer()
        this.performAttack()
      } else if (isLongPressed) {
        // 長押し解除 → 特殊攻撃終了
        endSpecial()
      }
    })
  }

  private setupMouseMovement() {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        if (this.ui.visible) {
          this.ui.next()
          return
        }
        if (this.npcManager.tryInteract(this.player, 80)) return
        this.isRightMouseHeld = true
        this.performAttack()
      }
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonReleased()) {
        this.isRightMouseHeld = false
        if (this.isSpecialAttacking) {
          this.isSpecialAttacking = false
          this.player.play(`hero-idle-${this.playerDirection}`)
        }
      }
    })

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.leftButtonDown()) {
        this.isMouseMoving = true
        this.updateMouseWorldPosition(pointer)
      }
    })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isMouseMoving && pointer.leftButtonDown()) {
        this.updateMouseWorldPosition(pointer)
      }
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.leftButtonDown()) {
        this.isMouseMoving = false
      }
    })

    this.input.mouse?.disableContextMenu()

    console.log('[MainScene] Mouse movement controls setup complete')
  }

  private updateMouseWorldPosition(pointer: Phaser.Input.Pointer) {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y)
    this.mouseWorldX = worldPoint.x
    this.mouseWorldY = worldPoint.y
  }

  update(time: number, delta: number) {
    // 初回 update でイントロを起動（scene が RUNNING 状態であることを保証）
    if (!this.introLaunched) {
      this.introLaunched = true
      const start = this.gameFlowManager.getStartConfig()
      if (start.story) {
        this.launchStory(start.story, start.then)
      } else {
        this.executeThen(start.then)
      }
      return
    }

    // Escキーでポーズメニューの表示/非表示を切り替え
    if (this.escKey && Phaser.Input.Keyboard.JustDown(this.escKey)) {
      if (this.pauseMenu) {
        if (this.pauseMenu.isShowing()) {
          this.pauseMenu.hide()
        } else {
          this.pauseMenu.show()
        }
      }
    }

    // ポーズメニュー表示中は処理を停止
    if (this.pauseMenu && this.pauseMenu.isShowing()) {
      this.player.setVelocity(0)
      this.isMouseMoving = false
      return
    }

    // ゲームオーバー時は処理を停止
    if (this.isGameOver) {
      this.player.setVelocity(0)
      return
    }

    if (this.ui.visible) {
      this.player.setVelocity(0)
      if (!this.isAttacking && !this.isSpecialAttacking) {
        this.player.anims.pause()
      }
      return
    } else {
      if (!this.isAttacking && !this.isSpecialAttacking) {
        const idleAnim = `hero-idle-${this.playerDirection}`
        if (this.player.anims.isPaused && this.player.anims.currentAnim?.key === idleAnim) {
          this.player.anims.resume()
        }
      }
    }

    // ボスAI更新
    if (this.boss && this.boss.state !== 'defeated') {
      updateBossAI(
        this,
        this.boss,
        this.player,
        this.projectiles,
        this.audioBus,
        this.cutinSystem,
        this.bossSpeechBubble,
        time,
        delta
      )

      if (this.bossHpUI) {
        this.bossHpUI.update(this.boss.hp, this.boss.maxHp, this.boss.phase)
      }

      // フェーズに応じて危険度を更新
      setDangerLevel(this.boss.phase >= 2 ? 3 : 2)

      // ボスアニメーション更新
      if (this.boss.animKey) {
        const vx = this.boss.body.velocity.x
        const vy = this.boss.body.velocity.y
        const moving = Math.abs(vx) > 10 || Math.abs(vy) > 10
        const moveDir = getDirectionFromVelocity(vx, vy)
        if (moving) this.boss.setData('lastDir', moveDir)
        const lastDir = this.boss.getData('lastDir') || 'down'

        if (this.boss.getData('dashActive')) {
          // 突進中: 移動方向の攻撃アニメ
          const atkAnim = `${this.boss.animKey}-atk-${moveDir}`
          if (this.boss.anims.currentAnim?.key !== atkAnim) this.boss.play(atkAnim, true)
        } else if (this.boss.state === 'attacking' || this.boss.state === 'cutin') {
          // 攻撃モーション中: プレイヤー方向の攻撃アニメ
          const atkDir = this.getDirectionToPlayer(this.boss)
          const atkAnim = `${this.boss.animKey}-atk-${atkDir}`
          if (this.boss.anims.currentAnim?.key !== atkAnim) this.boss.play(atkAnim, false)
        } else if (moving) {
          const walkAnim = `${this.boss.animKey}-walk-${moveDir}`
          if (this.boss.anims.currentAnim?.key !== walkAnim) this.boss.play(walkAnim, true)
        } else {
          const idleAnim = `${this.boss.animKey}-idle-${lastDir}`
          if (this.boss.anims.currentAnim?.key !== idleAnim && !this.boss.anims.currentAnim?.key.includes('atk')) {
            this.boss.play(idleAnim, true)
          }
        }
      }
    }

    // 誘導魔法弾の更新
    updateHomingOrbs(this)
    updateRotatingProjectiles(this)

    // 火炎放射の更新
    this.updateFireBalls(time)

    // イベントトリガーのチェック
    if (this.eventTriggerManager && !this.ui.visible) {
      const result = this.eventTriggerManager.checkTrigger(
        this.player.x,
        this.player.y,
        TILE
      )

      if (result && result.type === 'story' && result.storyId) {
        console.log(`[MainScene] Story event triggered: ${result.storyId}`)
        this.launchStory(result.storyId, result.then ?? { action: 'stay' })
      }
    }

    // ウィンドウ外でボタンを離した場合にフラグが残らないよう同期
    if (this.isRightMouseHeld && !this.input.activePointer.rightButtonDown()) {
      this.isRightMouseHeld = false
      if (this.isSpecialAttacking && !this.isMobileSpecialHeld) {
        this.isSpecialAttacking = false
        this.player.play(`hero-idle-${this.playerDirection}`)
      }
    }

    // 攻撃中・特殊攻撃中は移動不可
    // プレイヤー移動（攻撃中はスキップ）
    if (this.isAttacking || this.isSpecialAttacking) {
      this.player.setVelocity(0)
    } else {
      const speed: number = (this.player as any).speed

      let vx = ((this.cursors.left?.isDown || this.wasd.left.isDown) ? -1 : (this.cursors.right?.isDown || this.wasd.right.isDown) ? 1 : 0)
      let vy = ((this.cursors.up?.isDown   || this.wasd.up.isDown)   ? -1 : (this.cursors.down?.isDown  || this.wasd.down.isDown)  ? 1 : 0)

      // タッチ: ジョイスティックベクトルで移動
      if (vx === 0 && vy === 0 && this.virtualJoystick?.isActive) {
        vx = this.virtualJoystick.vector.x
        vy = this.virtualJoystick.vector.y
      }

      // PC: マウスクリック移動
      if (vx === 0 && vy === 0 && this.isMouseMoving) {
        const dx = this.mouseWorldX - this.player.x
        const dy = this.mouseWorldY - this.player.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance > 20) {
          vx = dx / distance
          vy = dy / distance
        }
      }

      const v = new Phaser.Math.Vector2(vx, vy)

      if (v.lengthSq() > 0) {
        v.normalize().scale(speed)
        this.player.setVelocity(v.x, v.y)

        const direction = getDirectionFromVelocity(v.x, v.y)
        this.playerDirection = direction

        const targetAnim = `hero-walk-${direction}`
        if (this.player.anims.currentAnim?.key !== targetAnim) {
          this.player.play(targetAnim, true)
        }
      } else {
        this.player.setVelocity(0, 0)
        const idleAnim = `hero-idle-${this.playerDirection}`
        const currentKey = this.player.anims.currentAnim?.key
        if (currentKey !== idleAnim) {
          this.player.play(idleAnim, true)
        }
      }
    }

    // 敵のAI更新とアニメ
    this.enemies.forEach(en => {
      if (!en.active) return

      if (en.getData('dead')) return
      if (en.getData('dying')) {
        en.setVelocity(0, 0)
        if (en.anims.currentAnim?.key !== `${en.animKey}-dying`) en.play(`${en.animKey}-dying`, true)
        return
      }

      updateEnemyAI(this, en, this.player)
      en.speech?.update(en)

      if (en.body.velocity.x !== 0 || en.body.velocity.y !== 0) {
        const dir = getDirectionFromVelocity(en.body.velocity.x, en.body.velocity.y)
        en.setData('dir', dir)
        const targetAnim = `${en.animKey}-walk-${dir}`
        if (en.anims.currentAnim?.key !== targetAnim) en.play(targetAnim, true)
      } else {
        const dir = en.getData('dir') || 'down'
        const idleAnim = `${en.animKey}-idle-${dir}`
        if (en.anims.currentAnim?.key !== idleAnim) en.play(idleAnim, true)
      }
    })

    // Archerの更新
    this.archers.forEach(archer => {
      if (!archer.active) return

      if (archer.getData('dead')) return
      if (archer.getData('dying')) {
        archer.setVelocity(0, 0)
        if (archer.anims.currentAnim?.key !== `${archer.animKey}-dying`) archer.play(`${archer.animKey}-dying`, true)
        return
      }

      updateArcherAI(this, archer, this.player)
      archer.speech?.update(archer)

      if (archer.body.velocity.x !== 0 || archer.body.velocity.y !== 0) {
        const dir = getDirectionFromVelocity(archer.body.velocity.x, archer.body.velocity.y)
        archer.setData('dir', dir)
        const targetAnim = `${archer.animKey}-walk-${dir}`
        if (archer.anims.currentAnim?.key !== targetAnim) archer.play(targetAnim, true)
      } else {
        if (archer.state === 'aim' || archer.state === 'shoot') {
          const dir = this.getDirectionToPlayer(archer)
          const targetAnim = `${archer.animKey}-atk-${dir}`
          if (archer.anims.currentAnim?.key !== targetAnim) archer.play(targetAnim, false)
        } else {
          const dir = archer.getData('dir') || 'down'
          const idleAnim = `${archer.animKey}-idle-${dir}`
          if (archer.anims.currentAnim?.key !== idleAnim && !archer.anims.currentAnim?.key.includes('atk')) {
            archer.play(idleAnim, true)
          }
        }
      }
    })

    // Mageの更新
    this.mages.forEach(mage => {
      if (!mage.active) return

      if (mage.getData('dead')) return
      if (mage.getData('dying')) {
        mage.setVelocity(0, 0)
        if (mage.anims.currentAnim?.key !== `${mage.animKey}-dying`) mage.play(`${mage.animKey}-dying`, true)
        return
      }

      updateMageAI(this, mage, this.player)
      mage.speech?.update(mage)

      if (mage.body.velocity.x !== 0 || mage.body.velocity.y !== 0) {
        const dir = getDirectionFromVelocity(mage.body.velocity.x, mage.body.velocity.y)
        mage.setData('dir', dir)
        const targetAnim = `${mage.animKey}-walk-${dir}`
        if (mage.anims.currentAnim?.key !== targetAnim) mage.play(targetAnim, true)
      } else {
        if (mage.state === 'aim' || mage.state === 'cast' || mage.state === 'shoot') {
          const dir = this.getDirectionToPlayer(mage)
          const targetAnim = `${mage.animKey}-atk-${dir}`
          if (mage.anims.currentAnim?.key !== targetAnim) mage.play(targetAnim, false)
        } else {
          const dir = mage.getData('dir') || 'down'
          const idleAnim = `${mage.animKey}-idle-${dir}`
          if (mage.anims.currentAnim?.key !== idleAnim && !mage.anims.currentAnim?.key.includes('atk')) {
            mage.play(idleAnim, true)
          }
        }
      }
    })

    // Bruteの更新
    this.brutes.forEach(brute => {
      if (!brute.active) return

      if (brute.getData('dead')) return
      if (brute.getData('dying')) {
        brute.setVelocity(0, 0)
        if (brute.anims.currentAnim?.key !== `${brute.animKey}-dying`) brute.play(`${brute.animKey}-dying`, true)
        return
      }

      updateBruteAI(this, brute, this.player)
      brute.speech?.update(brute)


      if (brute.state === 'dash') {
        const dir = getDirectionFromVelocity(brute.body.velocity.x, brute.body.velocity.y)
        const targetAnim = `${brute.animKey}-atk-${dir}`
        if (brute.anims.currentAnim?.key !== targetAnim) brute.play(targetAnim, true)
      } else if (brute.body.velocity.x !== 0 || brute.body.velocity.y !== 0) {
        const dir = getDirectionFromVelocity(brute.body.velocity.x, brute.body.velocity.y)
        brute.setData('dir', dir)
        const targetAnim = `${brute.animKey}-walk-${dir}`
        if (brute.anims.currentAnim?.key !== targetAnim) brute.play(targetAnim, true)
      } else {
        const dir = brute.getData('dir') || 'down'
        const idleAnim = `${brute.animKey}-idle-${dir}`
        if (brute.anims.currentAnim?.key !== idleAnim && !brute.anims.currentAnim?.key.includes('atk')) {
          brute.play(idleAnim, true)
        }
      }
    })

    // ゲーム内時計を進める
    advanceClock(delta)

    // NPC更新（移動・吹き出し）
    this.npcManager.update(delta, this.player.x, this.player.y)
  }

  /**
   * ストーリーを起動してゲームを一時停止する
   */
  private launchStory(storyId: string, then: ThenAction) {
    console.log(`[MainScene] launchStory: ${storyId}`, then)
    events.emit('story-start')
    this.scene.launch('StoryScene', { id: storyId, then })
    this.scene.pause()

    events.once('story:end', (data: { id: string; then: ThenAction }) => {
      console.log('[MainScene] story:end received, then:', data.then)
      this.executeThen(data.then)
    })
  }

  /**
   * ThenAction に従ってフロー制御を実行する
   */
  private executeThen(then: ThenAction) {
    console.log('[MainScene] executeThen:', then)
    switch (then.action) {
      case 'stay':
        this.scene.resume()
        break
      case 'exit':
        this.scene.stop()
        this.scene.start('TitleScene')
        break
      case 'goto_map':
        this.scene.resume()
        this.switchMap(then.mapId, then.x, then.y)
        break
      case 'goto_chapter':
        this.scene.stop()
        this.scene.start('ChapterLoadingScene', { chapterId: then.chapterId })
        break
    }
  }

  /**
   * ゲームクリア処理（ボス撃破時などに呼び出す）
   */
  private triggerGameClear() {
    console.log('triggerGameClear called!')

    if (this.isGameCleared) {
      return
    }

    this.isGameCleared = true
    this.player.setVelocity(0)

    const config = this.gameFlowManager.getMapConfig(this.currentMapId)
    const bossDefeat = config?.onBossDefeat ?? { story: 'clear', then: { action: 'exit' } as ThenAction }

    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      console.log('[MainScene] Fade out complete, launching clear story')
      if (bossDefeat.story) {
        this.launchStory(bossDefeat.story, bossDefeat.then)
      } else {
        this.executeThen(bossDefeat.then)
      }
    })

    this.cameras.main.fadeOut(800, 0, 0, 0)
  }

  private getDirectionToPlayer(enemy: Phaser.GameObjects.Sprite): string {
    const dx = this.player.x - enemy.x
    const dy = this.player.y - enemy.y
    return getDirectionFromVelocity(dx, dy)
  }

  private makeAnimatedEnemy(x: number, y: number, overrides?: EnemyOverrides): EnemyWithAI {
    const en = makeEnemy(this, x, y, overrides)
    en.setScale(1)
    en.lastSpeechTime = 0
    en.lastSpeechState = ''
    en.play(`${en.animKey}-idle-down`)
    if (overrides?.dialogs) {
      en.speech = new EnemySpeech(this)
    }
    this.addWorldObject(en)
    if (en.speech) this.addWorldObject(en.speech.getContainer())
    return en
  }

  private makeAnimatedArcher(x: number, y: number, overrides?: EnemyOverrides): Archer {
    const archer = makeArcher(this, x, y, overrides)
    archer.play(`${archer.animKey}-idle-down`)
    if (overrides?.dialogs) {
      archer.speech = new EnemySpeech(this)
    }
    this.addWorldObject(archer)
    if (archer.speech) this.addWorldObject(archer.speech.getContainer())
    return archer
  }

  private makeAnimatedMage(x: number, y: number, overrides?: EnemyOverrides): Mage {
    const mage = makeMage(this, x, y, overrides)
    mage.play(`${mage.animKey}-idle-down`)
    if (overrides?.dialogs) {
      mage.speech = new EnemySpeech(this)
    }
    this.addWorldObject(mage)
    if (mage.speech) this.addWorldObject(mage.speech.getContainer())
    return mage
  }

  private makeAnimatedBrute(x: number, y: number, overrides?: EnemyOverrides): Brute {
    const brute = makeBrute(this, x, y, overrides)
    brute.play(`${brute.animKey}-idle-down`)
    if (overrides?.dialogs) {
      brute.speech = new EnemySpeech(this)
    }
    this.addWorldObject(brute)
    if (brute.speech) this.addWorldObject(brute.speech.getContainer())
    return brute
  }

  private doAttack() {
    if (this.isAttacking) return

    this.isAttacking = true
    this.audioBus.playSe('se_player_attack', { volume: 0.8 })

    this.player.play(`hero-atk-${this.playerDirection}`)

    const dir = this.getFacingVector()
    const off = 40
    this.hitbox.x = this.player.x + dir.x * off
    this.hitbox.y = this.player.y + dir.y * off

    this.time.delayedCall(300, () => {
      ;(this.hitbox.body as Phaser.Physics.Arcade.Body).setEnable(false)
    })

    // 安全タイムアウト: ANIMATION_COMPLETE が何らかの理由で発火しなかった場合に強制クリア
    this.time.delayedCall(800, () => {
      if (this.isAttacking) {
        console.warn('[MainScene] isAttacking safety timeout triggered')
        this.isAttacking = false
      }
    })
  }

  private performAttack() {
    this.doAttack()
  }

  private spawnFireBall(): void {
    const fb = this.firePool.get(this.player.x, this.player.y, 'magic_fire') as FireBall | null
    if (!fb) return

    fb.setActive(true).setVisible(true).setDepth(5)
    fb.setScale(0.1)
    fb.damage = 1
    fb.originX = this.player.x
    fb.originY = this.player.y

    let dir: Phaser.Math.Vector2
    if (this.isTouchDevice) {
      const av = this.mobileAimVector
      const jv = this.virtualJoystick?.vector
      if (Math.abs(av.x) > 0.1 || Math.abs(av.y) > 0.1) {
        dir = new Phaser.Math.Vector2(av.x, av.y)
      } else if (jv && (Math.abs(jv.x) > 0.1 || Math.abs(jv.y) > 0.1)) {
        dir = new Phaser.Math.Vector2(jv.x, jv.y)
      } else {
        const dirMap: Record<string, [number, number]> = {
          up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0],
        }
        const [ddx, ddy] = dirMap[this.playerDirection] ?? [0, 1]
        dir = new Phaser.Math.Vector2(ddx, ddy)
      }
    } else {
      const ptr = this.input.activePointer
      const worldMouse = this.cameras.main.getWorldPoint(ptr.x, ptr.y)
      dir = new Phaser.Math.Vector2(worldMouse.x - this.player.x, worldMouse.y - this.player.y).normalize()
    }
    fb.setVelocity(dir.x * this.FIRE_SPEED, dir.y * this.FIRE_SPEED)
    fb.setRotation(Phaser.Math.Angle.Between(0, 0, dir.x, dir.y))

    if (fb.body) {
      (fb.body as Phaser.Physics.Arcade.Body).setEnable(true)
    }
    this.addWorldObject(fb)
  }

  private updateFireBalls(now: number): void {
    // 発射ループ
    if (this.isSpecialAttacking && (this.isRightMouseHeld || this.isMobileSpecialHeld)) {
      // 方向更新: PC=マウス座標, モバイル=移動タッチ座標（mouseWorldX/Y）
      let aimX: number, aimY: number
      if (this.isRightMouseHeld) {
        const ptr = this.input.activePointer
        const worldMouse = this.cameras.main.getWorldPoint(ptr.x, ptr.y)
        aimX = worldMouse.x
        aimY = worldMouse.y
      } else {
        // モバイル: 右ジョイスティック → 左ジョイスティック → プレイヤーの向き の優先順位で照準
        const av = this.mobileAimVector
        const jv = this.virtualJoystick?.vector
        if (Math.abs(av.x) > 0.1 || Math.abs(av.y) > 0.1) {
          aimX = this.player.x + av.x * 200
          aimY = this.player.y + av.y * 200
        } else if (jv && (Math.abs(jv.x) > 0.1 || Math.abs(jv.y) > 0.1)) {
          aimX = this.player.x + jv.x * 200
          aimY = this.player.y + jv.y * 200
        } else {
          const dirMap: Record<string, [number, number]> = {
            up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0]
          }
          const [ddx, ddy] = dirMap[this.playerDirection] ?? [0, 1]
          aimX = this.player.x + ddx * 200
          aimY = this.player.y + ddy * 200
        }
      }
      const dx = aimX - this.player.x
      const dy = aimY - this.player.y
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        const newDir = getDirectionFromVelocity(dx, dy)
        if (newDir !== this.playerDirection) {
          this.playerDirection = newDir
          this.player.play(`hero-special-${this.playerDirection}`, true)
        }
      }

      if (now - this.lastFireTime >= this.FIRE_INTERVAL) {
        this.lastFireTime = now
        this.spawnFireBall()
      }
      if (!this.flameIsPlaying) {
        this.audioBus.playSeLoop('se_flame', 0.6)
        this.flameIsPlaying = true
      }
    } else if (this.flameIsPlaying) {
      this.audioBus.stopSeLoop('se_flame')
      this.flameIsPlaying = false
    }

    // 各弾の更新（飛距離チェック・スケール更新）
    this.firePool.getChildren().forEach(obj => {
      const fb = obj as FireBall
      if (!fb.active) return
      const dist = Phaser.Math.Distance.Between(fb.originX, fb.originY, fb.x, fb.y)
      if (dist >= this.FIRE_RANGE) {
        this.firePool.killAndHide(fb)
        if (fb.body) (fb.body as Phaser.Physics.Arcade.Body).setEnable(false)
        return
      }
      fb.setScale(Math.min(1.0, 0.1 + 0.9 * (dist / this.FIRE_RANGE)))
    })
  }

  private getFacingVector() {
    if (this.playerDirection === 'left') return new Phaser.Math.Vector2(-1, 0)
    if (this.playerDirection === 'right') return new Phaser.Math.Vector2(1, 0)
    if (this.playerDirection === 'up') return new Phaser.Math.Vector2(0, -1)
    if (this.playerDirection === 'down') return new Phaser.Math.Vector2(0, 1)
    return new Phaser.Math.Vector2(0, 1)
  }

  private updateHPDisplay() {
    if (!this.player || !this.hpText) {
      console.warn('updateHPDisplay: player or hpText is null!', { player: this.player, hpText: this.hpText })
      return
    }

    const currentHP = (this.player as any).hp || 0
    const maxHP = 100

    const barLength = 20
    const filledCount = Math.floor((currentHP / maxHP) * barLength)
    const filledBars = '█'.repeat(Math.max(0, filledCount))
    const emptyBars = '░'.repeat(Math.max(0, barLength - filledCount))

    const newText = `HP: ${currentHP}/${maxHP} [${filledBars}${emptyBars}]`
    this.hpText.setText(newText)

    if (currentHP <= 20) {
      this.hpText.setColor('#ff0000')
    } else if (currentHP <= 40) {
      this.hpText.setColor('#ffaa00')
    } else {
      this.hpText.setColor('#ffffff')
    }
  }

  private triggerGameOver() {
    console.log('triggerGameOver called! isGameOver:', this.isGameOver)

    if (this.isGameOver) {
      return
    }

    this.isGameOver = true
    console.log('=== GAME OVER ===')

    this.player.setVelocity(0)

    const config = this.gameFlowManager.getMapConfig(this.currentMapId)
    const defeat = config?.onPlayerDefeat ?? { story: 'gameover', then: { action: 'exit' } as ThenAction }

    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      console.log('[MainScene] Fade out complete, launching gameover story')
      if (defeat.story) {
        this.launchStory(defeat.story, defeat.then)
      } else {
        this.executeThen(defeat.then)
      }
    })

    this.cameras.main.fadeOut(800, 0, 0, 0)
  }

  /**
   * 敵とイベントトリガーを初期化
   */
  private initializeEnemiesAndTriggers(mapData: Record<string, unknown>) {
    type EnemySpawn = { x: number; y: number; enemyDefId?: string }
    type EnemyDef = {
      id: string
      enemyType: string
      spriteKey: string
      animPrefix: string
      stats: Record<string, number>
      dialogs?: EnemyDialogs
      hitSound?: string
      attackSound?: string
    }

    const defs = (this.cache.json.get('enemy-defs') as EnemyDef[]) || []
    const enemySpawns = (mapData.enemySpawns as EnemySpawn[]) || []

    // enemyType → デフォルトanimKeyのマッピング
    const defaultAnimKey: Record<string, string> = {
      blob: 'blob', archer: 'archer', mage: 'mage', brute: 'brute'
    }

    enemySpawns.forEach((spawn) => {
      const def = spawn.enemyDefId ? defs.find(d => d.id === spawn.enemyDefId) : undefined
      // spriteKeyが存在しテクスチャが読み込まれていればそれをanimKeyとして使用
      const animKey = def?.spriteKey && this.textures.exists(def.spriteKey)
        ? def.spriteKey
        : (def?.enemyType ? (defaultAnimKey[def.enemyType] ?? def.enemyType) : undefined)
      const overrides: EnemyOverrides | undefined = def
        ? {
            ...def.stats,
            dialogs: def.dialogs,
            ...(animKey ? { animKey } : {}),
            ...(def.hitSound    ? { hitSound: def.hitSound }       : {}),
            ...(def.attackSound ? { attackSound: def.attackSound } : {}),
          }
        : undefined
      const resolvedType = def?.enemyType ?? (['blob', 'archer', 'mage', 'brute'] as const)[Math.floor(Math.random() * 4)]

      const px = spawn.x * TILE
      const py = spawn.y * TILE
      if (resolvedType === 'blob') {
        this.enemies.push(this.makeAnimatedEnemy(px, py, overrides))
      } else if (resolvedType === 'archer') {
        this.archers.push(this.makeAnimatedArcher(px, py, overrides))
      } else if (resolvedType === 'mage') {
        this.mages.push(this.makeAnimatedMage(px, py, overrides))
      } else {
        this.brutes.push(this.makeAnimatedBrute(px, py, overrides))
      }
    })

    // 敵と壁の衝突判定を設定
    this.enemies.forEach(en => {
      const collider = this.physics.add.collider(en, this.walls!)
      this.colliders.push(collider)
      const hitCollider = this.physics.add.overlap(this.hitbox, en, () => {
        if (!en.getData('hitCool') && !en.getData('dead')) {
          en.hp -= 1
          en.setTint(0xff4444)
          const enHitKey = (en as any).hitSound ? 'se_' + (en as any).hitSound.replace(/\.(ogg|mp3)$/, '') : 'se_enemy_hit'
          this.audioBus.playSe(enHitKey, { volume: 0.8 })
          en.setData('hitCool', true)
          this.time.delayedCall(100, () => en.clearTint())
          this.time.delayedCall(250, () => en.setData('hitCool', false))
          if (en.hp <= 0) {
            if (!en.getData('dead')) this.triggerEnemyDeath(en)
          } else if (en.hp <= 10 && !en.getData('dying')) {
            en.setData('dying', true)
            en.setVelocity(0, 0)
          }
        }
      })
      this.colliders.push(hitCollider)
      const contactCollider = this.physics.add.overlap(this.player, en, () => {
        if (en.state !== 'chase' && en.state !== 'attack') return
        if (en.getData('dead')) return
        if (this.player.getData('hitCool') || this.isGameOver) return
        type PlayerWithHp = Phaser.Types.Physics.Arcade.SpriteWithDynamicBody & { hp: number }
        const damage = (en as EnemyWithAI & { damage?: number }).damage ?? 1
        const oldHP = (this.player as PlayerWithHp).hp
        const newHP = Math.max(0, oldHP - damage)
        ;(this.player as PlayerWithHp).hp = newHP
        this.updateHPDisplay()
        this.player.setTint(0xff4444)
        this.audioBus.playSe('se_player_hit', { volume: 0.8 })
        this.player.setData('hitCool', true)
        this.time.delayedCall(100, () => this.player.clearTint())
        this.time.delayedCall(500, () => this.player.setData('hitCool', false))
        if (newHP <= 0) this.triggerGameOver()
      })
      this.colliders.push(contactCollider)
    })
    this.archers.forEach(ar => {
      const collider = this.physics.add.collider(ar, this.walls!)
      this.colliders.push(collider)
      this.setupEnemyHit(ar)
    })
    this.mages.forEach(mg => {
      const collider = this.physics.add.collider(mg, this.walls!)
      this.colliders.push(collider)
      this.setupEnemyHit(mg)
    })
    this.brutes.forEach(br => {
      const collider = this.physics.add.collider(br, this.walls!)
      this.colliders.push(collider)
      this.setupEnemyHit(br)
    })

    // 火炎弾 vs 壁・敵
    if (this.walls) {
      const fireVsWall = this.physics.add.overlap(this.firePool, this.walls, (fb) => {
        this.firePool.killAndHide(fb as Phaser.GameObjects.GameObject)
        if ((fb as FireBall).body) ((fb as FireBall).body as Phaser.Physics.Arcade.Body).setEnable(false)
      })
      this.colliders.push(fireVsWall)
    }

    const allEnemyGroups = [...this.enemies, ...this.archers, ...this.mages, ...this.brutes]
    allEnemyGroups.forEach(en => {
      const fireVsEnemy = this.physics.add.overlap(this.firePool, en, (_enCb, fb) => {
        this.firePool.killAndHide(fb as Phaser.GameObjects.GameObject)
        if ((fb as FireBall).body) ((fb as FireBall).body as Phaser.Physics.Arcade.Body).setEnable(false)
        if (!en.getData('hitCool') && !en.getData('dead')) {
          en.hp -= 1
          en.setTint(0xff4444)
          const hitKey = (en as any).hitSound ? 'se_' + (en as any).hitSound.replace(/\.(ogg|mp3)$/, '') : 'se_enemy_hit'
          this.audioBus.playSe(hitKey, { volume: 0.8 })
          en.setData('hitCool', true)
          this.time.delayedCall(100, () => en.clearTint())
          this.time.delayedCall(250, () => en.setData('hitCool', false))
          if (en.hp <= 0 && !en.getData('dead')) this.triggerEnemyDeath(en)
        }
      })
      this.colliders.push(fireVsEnemy)
    })

    // イベントトリガーを gameflow.json から取得して初期化
    const eventTriggers = this.gameFlowManager.getEventTriggers(this.currentMapId)
    if (eventTriggers.length > 0) {
      this.eventTriggerManager = new EventTriggerManager(this, eventTriggers, TILE, this.currentMapId)
      console.log(`[MainScene] Initialized ${eventTriggers.length} event triggers for ${this.currentMapId}`)
    }

    // PortalManager を初期化（map JSON の portals と gameflow.json の portals をインデックス突合）
    const gameflowPortals = this.gameFlowManager.getPortals(this.currentMapId)
    const portalDefs = (this.cache.json.get('portal-defs') as Array<{ key: string; animated: boolean; frameCount: number; frameRate: number }> | null) ?? []
    const mapPortalPositions = ((this.currentMapData as Record<string, unknown>).portals as Array<{ x: number; y: number; spriteKey?: string; rotation?: number }> | undefined) ?? []
    const fullPortals: FullPortalData[] = mapPortalPositions
      .map((pos, i) => {
        const dest = gameflowPortals[i]
        if (!dest) return null
        const entry: FullPortalData = { x: pos.x, y: pos.y, targetMap: dest.targetMap, targetX: dest.targetX, targetY: dest.targetY }
        if (pos.spriteKey) {
          entry.spriteKey = pos.spriteKey
          const def = portalDefs.find(d => d.key === pos.spriteKey)
          if (def) {
            entry.animated = def.animated
            entry.frameCount = def.frameCount
            entry.frameRate = def.frameRate
          }
        }
        if (pos.rotation) entry.rotation = pos.rotation
        return entry
      })
      .filter((p): p is FullPortalData => p !== null)

    if (fullPortals.length > 0) {
      this.portalManager = new PortalManager(this, fullPortals, TILE)
      this.portalManager.setupOverlap(this.player, (p: FullPortalData) => {
        if (this.isGameOver || this.ui.visible) return
        console.log(`[MainScene] Portal triggered: ${p.targetMap} (${p.targetX}, ${p.targetY})`)
        this.switchMap(p.targetMap, p.targetX, p.targetY)
      })
      console.log(`[MainScene] Initialized ${fullPortals.length} portals for ${this.currentMapId}`)
    }
  }

  /**
   * 敵の死亡処理
   */
  private triggerEnemyDeath(en: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody) {
    en.setData('dead', true)
    en.setData('dying', false)
    en.setVelocity(0, 0)
    en.clearTint()

    const key = en.texture.key
    const prefixMap: Record<string, string> = {
      solder: 'blob',
      vamp1: 'archer',
      succubus: 'mage',
      mage: 'brute',
    }
    const prefix = prefixMap[key] ?? 'blob'
    en.play(`${prefix}-dead`)
    en.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      en.disableBody(true, true)
    })
  }

  /**
   * 敵との攻撃判定を設定
   */
  private setupEnemyHit(en: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody & { hp: number }) {
    const hitCollider = this.physics.add.overlap(this.hitbox, en, () => {
      if (!en.getData('hitCool') && !en.getData('dead')) {
        en.hp -= 1
        en.setTint(0xff4444)
        const hitKey = (en as any).hitSound ? 'se_' + (en as any).hitSound.replace(/\.(ogg|mp3)$/, '') : 'se_enemy_hit'
        this.audioBus.playSe(hitKey, { volume: 0.8 })
        en.setData('hitCool', true)
        this.time.delayedCall(100, () => en.clearTint())
        this.time.delayedCall(250, () => en.setData('hitCool', false))

        if (en.hp <= 0) {
          if (!en.getData('dead')) this.triggerEnemyDeath(en)
        } else if (en.hp <= 10 && !en.getData('dying')) {
          en.setData('dying', true)
          en.setVelocity(0, 0)
        }
      }
    })
    this.colliders.push(hitCollider)

    if ((en as any).enemyType === 'brute') {
      const bruteCollider = this.physics.add.overlap(this.player, en, () => {
        const brute = en as Brute
        if (brute.state === 'dash' && !this.player.getData('hitCool') && !this.isGameOver) {
          const oldHP = (this.player as any).hp
          ;(this.player as any).hp = Math.max(0, oldHP - 2)
          const newHP = (this.player as any).hp
          console.log(`Player hit by Brute dash! HP: ${oldHP} -> ${newHP}`)
          this.updateHPDisplay()
          this.player.setTint(0xff4444)
          this.audioBus.playSe('se_player_hit', { volume: 0.8 })
          this.player.setData('hitCool', true)
          this.time.delayedCall(100, () => this.player.clearTint())
          this.time.delayedCall(1000, () => this.player.setData('hitCool', false))

          if (newHP <= 0) {
            this.triggerGameOver()
          }
        }
      })
      this.colliders.push(bruteCollider)
    }
  }

  /**
   * マップを切り替える
   */
  private switchMap(mapId: string, targetX: number, targetY: number) {
    console.log(`[MainScene] Switching to map: ${mapId} at (${targetX}, ${targetY})`)

    // すべての衝突判定を破棄
    this.colliders.forEach(collider => {
      if (collider && collider.active) {
        collider.destroy()
      }
    })
    this.colliders = []

    // 既存の敵を破棄
    this.enemies.forEach(enemy => { if (enemy) { enemy.speech?.destroy(); enemy.destroy() } })
    this.enemies = []

    this.archers.forEach(archer => { if (archer) { archer.speech?.destroy(); archer.destroy() } })
    this.archers = []

    this.mages.forEach(mage => { if (mage) { mage.speech?.destroy(); mage.destroy() } })
    this.mages = []

    this.brutes.forEach(brute => { if (brute) { brute.speech?.destroy(); brute.destroy() } })
    this.brutes = []

    // ボスとUIを破棄
    if (this.boss) {
      this.boss.destroy()
      this.boss = null
    }

    if (this.bossHpUI) {
      this.bossHpUI.destroy()
      this.bossHpUI = null
    }

    // 飛び道具を破棄
    if (this.projectiles) {
      this.projectiles.clear(true, true)
    }
    if (this.firePool) {
      this.firePool.clear(true, true)
    }

    // 既存のマップオブジェクト（床と壁）を破棄
    const tileSprites = this.children.list.filter(obj => obj.type === 'TileSprite')
    tileSprites.forEach(sprite => sprite.destroy())
    // 床キャンバス Image を破棄
    if (this.textures.exists('__floor_canvas__')) {
      const floorImg = this.children.list.find(
        obj => obj.type === 'Image' && (obj as Phaser.GameObjects.Image).texture?.key === '__floor_canvas__'
      ) as Phaser.GameObjects.Image | undefined
      floorImg?.destroy()
      this.textures.remove('__floor_canvas__')
    }

    if (this.walls) {
      this.walls.clear(true, true)
      this.walls.destroy()
    }

    // アニメタイルスプライトを破棄（前マップの残留防止）
    this.mapAnimSprites.forEach(s => s.destroy())
    this.mapAnimSprites = []

    // オブジェクト・オーバーレイレイヤースプライトを破棄
    this.mapLayerSprites.forEach(s => s.destroy())
    this.mapLayerSprites = []

    // イベントトリガーマネージャーを破棄
    if (this.eventTriggerManager) {
      this.eventTriggerManager.destroy()
      this.eventTriggerManager = undefined
    }

    // PortalManagerを破棄
    if (this.portalManager) {
      this.portalManager.destroy()
      this.portalManager = undefined
    }

    // NPCを破棄
    if (this.npcManager) {
      this.npcManager.destroy()
    }

    // ゲームオーバー・クリアフラグをリセット（新マップ遷移時）
    this.isGameOver = false
    this.isGameCleared = false

    // 新しいマップをロード
    this.loadMap(mapId)

    // プレイヤーを移動先に配置
    this.player.setPosition(targetX * TILE, targetY * TILE)
    this.player.setDepth(10)

    // カメラを追従させる
    this.cameras.main.startFollow(this.player, true, 0.12, 0.12)
    this.cameras.main.setZoom(1.5)

    // マップロード完了イベントを発火（BGM切り替え用）
    events.emit('map-loaded', mapId)
    console.log('[MainScene] Map loaded event emitted:', mapId)
  }

  /**
   * マップをロードして構築
   */
  private loadMap(mapId: string) {
    console.log(`[MainScene] Loading map: ${mapId}`)

    let mapData = this.cache.json.get(mapId) as Record<string, unknown> | undefined
    if (!mapData) {
      // キャッシュにない場合、同期XHRでフォールバック取得
      console.warn(`[MainScene] ${mapId} not in cache, fetching directly...`)
      try {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', `assets/maps/${mapId}.json`, false)
        xhr.send()
        if (xhr.status === 200) {
          mapData = JSON.parse(xhr.responseText) as Record<string, unknown>
          this.cache.json.add(mapId, mapData)
        } else {
          console.error(`Map data not found: ${mapId} (HTTP ${xhr.status})`)
          return
        }
      } catch (e) {
        console.error(`Map data not found: ${mapId}`, e)
        return
      }
    }

    this.currentMapId = mapId
    this.currentMapData = mapData

    // マップ遷移時に WorldState をリセット
    resetWorldState()

    // タイルマップを構築
    const tileDefArray = this.cache.json.get('tilesets') as TileDef[]
    const tileDefMap = new Map(tileDefArray.map(d => [d.id, d]))
    const result = buildMapFromJSON(this, mapData as unknown as MapData, tileDefMap)
    this.walls = result.walls
    this.mapAnimSprites = result.animSprites
    this.mapLayerSprites = result.layerSprites

    // マップタイル・壁・スプライトをUIカメラから除外
    result.walls.getChildren().forEach(w => this.addWorldObject(w))
    result.animSprites.forEach(s => this.addWorldObject(s))
    result.layerSprites.forEach(s => this.addWorldObject(s))
    const floorImg = this.children.list.find(
      obj => obj.type === 'Image' && (obj as Phaser.GameObjects.Image).texture?.key === '__floor_canvas__'
    ) as Phaser.GameObjects.Image | undefined
    if (floorImg) this.addWorldObject(floorImg)

    // カメラと物理世界の境界を更新
    this.cameras.main.setBounds(0, 0, result.worldW, result.worldH)
    this.physics.world.setBounds(0, 0, result.worldW, result.worldH)

    // プレイヤーと壁の衝突を設定
    const playerWallCollider = this.physics.add.collider(this.player, this.walls!)
    this.colliders.push(playerWallCollider)

    // ボス生成（gameflow.json の boss フィールドで判定）
    const mapConfig = this.gameFlowManager.getMapConfig(mapId)
    if (mapConfig?.boss) {
      this.spawnBoss()
    }

    // 敵とイベントトリガーを初期化
    this.initializeEnemiesAndTriggers(mapData)

    // NPCを再初期化
    const npcDefs = (this.cache.json.get('npc-defs') as NPCDef[]) || []
    const npcSpawns = (mapData.npcSpawns as NPCSpawn[]) || []
    const typedMapData = mapData as unknown as MapData
    this.npcManager.loadActivitySpots(typedMapData.activitySpots ?? [])
    this.npcManager.loadFromSpawns(npcSpawns, npcDefs)
    const npcColliders = this.npcManager.setupCollisions(this.player)
    this.colliders.push(...npcColliders)

    // NPCスプライト・吹き出しをUIカメラから除外
    this.npcManager.getGameObjects().forEach(go => this.addWorldObject(go))

    // onEnter ストーリーがあれば再生
    if (mapConfig?.onEnter?.story) {
      this.launchStory(mapConfig.onEnter.story, mapConfig.onEnter.then)
    }

    console.log(`[MainScene] Map loaded: ${mapId}`)
  }

  /**
   * ボス生成
   */
  private spawnBoss() {
    const mapConfig = this.gameFlowManager.getMapConfig(this.currentMapId)
    if (!mapConfig?.boss) return
    const { configKey, x, y } = mapConfig.boss
    console.log(`[MainScene] Spawning boss: ${configKey} at (${x}, ${y})`)

    this.boss = makeBoss(this, x * TILE, y * TILE, configKey)
    this.addWorldObject(this.boss)
    setDangerLevel(2)

    const bossWallCollider = this.physics.add.collider(this.boss, this.walls!)
    this.colliders.push(bossWallCollider)

    this.setupBossHit()

    this.bossHpUI = new BossHpUI(this, this.boss.name)
    this.cameras.main.ignore(this.bossHpUI.getContainer())
    this.bossHpUI.show()
    this.bossHpUI.update(this.boss.hp, this.boss.maxHp, this.boss.phase)

    console.log(`[MainScene] Boss spawned: ${this.boss.name}`)
  }

  /**
   * ボスとの攻撃判定を設定
   */
  private setupBossHit() {
    if (!this.boss) return

    const playerAttackCollider = this.physics.add.overlap(this.hitbox, this.boss, () => {
      if (!this.boss || !this.boss.active) return

      if (!this.boss.getData('hitCool')) {
        const oldHP = this.boss.hp
        this.boss.hp -= 1
        console.log(`[MainScene] Boss damaged! HP: ${oldHP} -> ${this.boss.hp} (Phase: ${this.boss.phase})`)
        this.boss.setTint(0xff4444)
        this.boss.setData('hitCool', true)

        if (this.boss.config.se.damage) {
          this.audioBus.playSe(this.boss.config.se.damage, { volume: 0.7 })
        }

        this.time.delayedCall(100, () => {
          if (this.boss) this.boss.clearTint()
        })
        this.time.delayedCall(250, () => {
          if (this.boss) this.boss.setData('hitCool', false)
        })

        if (this.boss.hp <= 0) {
          this.defeatBoss()
        }
      }
    })
    this.colliders.push(playerAttackCollider)

    const bossContactCollider = this.physics.add.overlap(this.player, this.boss, () => {
      if (!this.boss || !this.boss.active) return

      const dashDamage = this.boss.getData('dashDamage') || 0
      if (dashDamage > 0 && !this.player.getData('hitCool') && !this.isGameOver) {
        const oldHP = (this.player as any).hp
        ;(this.player as any).hp = Math.max(0, oldHP - dashDamage)

        console.log(`Player hit by boss dash! HP: ${oldHP} -> ${(this.player as any).hp}`)

        this.updateHPDisplay()
        this.player.setTint(0xff4444)
        this.audioBus.playSe('se_player_hit', { volume: 0.8 })
        this.player.setData('hitCool', true)
        this.time.delayedCall(100, () => this.player.clearTint())
        this.time.delayedCall(500, () => this.player.setData('hitCool', false))

        if ((this.player as any).hp <= 0) {
          this.triggerGameOver()
        }
      }
    })
    this.colliders.push(bossContactCollider)

    // 火炎弾 vs ボス
    if (this.firePool) {
      const fireVsBoss = this.physics.add.overlap(this.firePool, this.boss, (_bossCb, fb) => {
        if (!this.boss || !this.boss.active || !this.boss.getData) return
        this.firePool.killAndHide(fb as Phaser.GameObjects.GameObject)
        if ((fb as FireBall).body) ((fb as FireBall).body as Phaser.Physics.Arcade.Body).setEnable(false)
        if (!this.boss.getData('hitCool')) {
          this.boss.hp -= 1
          this.boss.setTint(0xff4444)
          this.boss.setData('hitCool', true)
          if (this.boss.config.se.damage) this.audioBus.playSe(this.boss.config.se.damage, { volume: 0.7 })
          this.time.delayedCall(100, () => { if (this.boss) this.boss.clearTint() })
          this.time.delayedCall(250, () => { if (this.boss) this.boss.setData('hitCool', false) })
          this.bossHpUI?.update(this.boss.hp, this.boss.maxHp, this.boss.phase)
          if (this.boss.hp <= 0) this.defeatBoss()
        }
      })
      this.colliders.push(fireVsBoss)
    }
  }

  /**
   * ボス撃破処理
   */
  private defeatBoss() {
    if (!this.boss) return

    console.log('[MainScene] Boss defeated!')

    this.boss.state = 'defeated'
    setDangerLevel(0)

    if (this.boss.config.se.defeat) {
      this.audioBus.playSe(this.boss.config.se.defeat, { volume: 0.8 })
    }

    if (this.boss.config.speeches.defeat) {
      const { defeat } = resolveText({ defeat: this.boss.config.speeches.defeat }, this.boss.config.speeches.i18n)
      this.bossSpeechBubble.show(this.boss, defeat!, 2000)
    }

    if (this.bossHpUI) {
      this.bossHpUI.hide()
    }

    this.tweens.add({
      targets: this.boss,
      alpha: 0,
      scale: 6,
      duration: 1500,
      onComplete: () => {
        if (this.boss) {
          this.boss.disableBody(true, true)
        }
        this.triggerGameClear()
      }
    })
  }
}
